import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Container, Tab, Tabs } from 'react-bootstrap'
import '@components/pages/Insurance/InsuranceMainStyle.css'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import {
  fetchInsurancePlansByPatientId,
  deleteInsurancePlanById,
  setPrimaryInsurancePlanById,
} from '@store/actions/insurance.action'
import { fetchAllPatients } from '@store/actions/patientmanagement.action'
import { InsuranceData } from '@dataTypes/insurance.types'
import { GridComponent, RowSelectEventArgs } from '@syncfusion/ej2-react-grids'
import AddInsurance from '@components/layout/Modals/AddInsurance/AddInsurance.component'
import SectionInsuranceAction from '@components/pages/Insurance/InsuranceAction/InsuranceAction.component'
import SectionInsurancePlan from '@components/pages/Insurance/InsurancePlan/InsurancePlan.component'
import SectionInsuranceDetails from '@components/pages/Insurance/PlanDetails/PlanDetails.component'
import SectionInsuranceCoverage from '@components/pages/Insurance/PlanCoverage/PlanCoverage.component'
import SectionClaimHistory from '@components/pages/Insurance/ClaimHistory/ClaimHistory.component'
import { v4 as uuid } from 'uuid'
import GoBackButton from '@components/common/GoBackButton/GoBackButton.component'
import {
  requestPatientSelection,
  toggleSidePanel,
} from '@store/reducers/uiSlice.reducer'
import { DeleteConfirmationModal } from '@components/layout/Modals'
import InsuranceEmptyCard from '@components/pages/Insurance/InsuranceEmptyCard/InsuranceEmptyCard.component'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createFailedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'
import { fetchPatientById } from '@services/api/patient.api'
import { PatientWithInsurance } from '@dataTypes/appointment.type'
import { setSelectedPatient } from '@store/reducers/patientmanagement.reducer'
import InsuranceSkeleton from './InsuranceSkeleton.page'
import {
  FIRST_LOAD_OVERLAY_MS,
  FIRST_LOAD_OVERLAY_STYLE,
  waitAtLeast,
} from '@utils/waitAtLeast.util'
import { logger } from '@utils/safeLogger'
const TAB_SKELETON_DURATION_MS = 500
const GLOBAL_LOADING_CONTAINER = 'body'
const INSURANCE_LOADING_CONTAINER = '.insurance-container'

const InsuranceMain: React.FC = (): React.ReactElement => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const insurancePlanData = (useAppSelector((state) => state.insurance.data) ||
    {}) as InsuranceData
  const insuranceLoading = useAppSelector((state) => state.insurance.loading)
  const insuranceData = useRef<InsuranceData>(insurancePlanData)
  const updatedInsuranceData = {
    ...insuranceData.current,
    insurancePlans: (insuranceData?.current?.insurancePlans || []).map(
      (plan) => ({
        ...plan,
      }),
    ),
  }
  insuranceData.current = updatedInsuranceData
  const insuranceState = insuranceData.current
  const [isAddInsuranceVisible, setIsAddInsuranceVisible] =
    useState<boolean>(false)
  const [, setRerender] = useState(0)
  const gridRef = useRef<GridComponent>(null)
  const selectedInsurancePlanIndex = useRef<number>(0)
  const uuId = useRef<string>(uuid())
  const isInsurancePlansProcessing = useRef<boolean>(false)
  const location = useLocation()
  const { tabKey, activeInsurancePlanId, previousPage } = location.state || {}
  const [currentTabKeyState, setCurrentTabKeyState] = useState<string>(
    tabKey || 'details',
  )
  const [isTabSkeleton, setIsTabSkeleton] = useState(false)
  const [tabSkeletonKey, setTabSkeletonKey] = useState(0)
  const [isPageReady, setIsPageReady] = useState(false)
  const isPageReadyRef = useRef(false)
  const prevTabRef = useRef(currentTabKeyState)
  const tabSkeletonStartedAtRef = useRef(Date.now())
  const [pagePreviousState, setPagePreviousState] = useState<any>(previousPage)
  const selectedPatient = useAppSelector(
    (state) => state.patient.selectedPatient,
  )
  const contacts = useAppSelector((state) => state.contacts.data)
  const defaultInsurancePlans = useRef<string[]>([
    '1e5a43c0-13a1-4bb1-963a-4e0f365d2e59',
    '72f2e5a2-8c9b-48ed-9399-0c9f1d1fe50c',
    '7e8a8c7f-f60e-4a6c-b39f-d1f003a1d634',
    'a58ad4c7-2c92-4f8b-bb4a-9b92b1f2e876',
  ])
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false)
  const [insurancePlanIdToDelete, setInsurancePlanIdToDelete] = useState('')
  const [selectedFamilyMemberId, setSelectedFamilyMemberId] = useState<
    string | undefined
  >(undefined)
  const [showFamilyMembers, setShowFamilyMembers] = useState(false)
  const prevClinicIdRef = useRef<string | undefined>(undefined)
  const selectedClinic = useAppSelector((state) => state.clinic.selectedClinic)
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const { showToast } = useNotificationToast()

  useEffect(() => {
    if (tabKey !== currentTabKeyState) {
      setCurrentTabKeyState(tabKey || 'details')
    }
  }, [tabKey])

  // On clinic change: show global loading, fetch patients for new clinic, then show Select Patient modal
  const clinicId = selectedClinic?.clinic_id
  useEffect(() => {
    const prevClinicId = prevClinicIdRef.current
    if (prevClinicId !== undefined && prevClinicId !== clinicId) {
      showLoading({ container: GLOBAL_LOADING_CONTAINER })
      dispatch(fetchAllPatients())
        .unwrap()
        .finally(() => {
          hideLoading(GLOBAL_LOADING_CONTAINER)
        })
    }
    prevClinicIdRef.current = clinicId
  }, [clinicId, dispatch, showLoading, hideLoading])

  const forceRerender = (): void => {
    setRerender((prev) => prev + 1)
  }

  useEffect(() => {
    if (!activeInsurancePlanId) return

    let interval: number | null = null
    let timeout: number | null = null

    interval = window.setInterval(() => {
      const index = insuranceData.current.insurancePlans.findIndex(
        (plan) => plan.insurance_plan_id === activeInsurancePlanId,
      )

      const tabContent = document.querySelector(
        '.insurance-container > .tab-content',
      )

      if (
        index !== -1 &&
        tabContent &&
        (tabContent as HTMLElement).style.visibility === 'visible'
      ) {
        selectedInsurancePlanIndex.current = index
        location.state.activeInsurancePlanId = undefined

        if (interval !== null) clearInterval(interval)
        if (timeout !== null) clearTimeout(timeout)

        if (currentTabKeyState === 'coverage') {
          uuId.current = uuid()
        }

        forceRerender()
      }
    }, 100)

    timeout = window.setTimeout(() => {
      location.state.activeInsurancePlanId = undefined
      if (interval !== null) clearInterval(interval)
    }, 10000)

    return () => {
      if (interval !== null) clearInterval(interval)
      if (timeout !== null) clearTimeout(timeout)
    }
  }, [activeInsurancePlanId])

  const optionalCompletionAction = useRef<any>(undefined)
  const displayPageLoading = useCallback(() => {
    showLoading({
      container: INSURANCE_LOADING_CONTAINER,
      style: FIRST_LOAD_OVERLAY_STYLE,
    })
  }, [showLoading])

  const displayLoading = useCallback(() => {
    showLoading({
      container: INSURANCE_LOADING_CONTAINER,
      hideElements: ['.insurance-container > .tab-content'],
    })
  }, [showLoading])

  const hideInsuranceLoading = useCallback((): void => {
    hideLoading(INSURANCE_LOADING_CONTAINER)
    if (optionalCompletionAction.current) {
      optionalCompletionAction.current()
      optionalCompletionAction.current = undefined
    }
  }, [hideLoading])

  useLayoutEffect(() => {
    if (isPageReadyRef.current && prevTabRef.current !== currentTabKeyState) {
      hideInsuranceLoading()
      tabSkeletonStartedAtRef.current = Date.now()
      setIsTabSkeleton(true)
      setTabSkeletonKey((key) => key + 1)
      if (selectedPatient?.patient_id) {
        void dispatch(
          fetchInsurancePlansByPatientId(selectedPatient.patient_id),
        ).then((state) => {
          if (!state.payload) return
          insuranceData.current = state.payload
          forceRerender()
        })
      }
    }
    prevTabRef.current = currentTabKeyState
  }, [
    currentTabKeyState,
    dispatch,
    hideInsuranceLoading,
    selectedPatient?.patient_id,
  ])

  useEffect(() => {
    if (!isTabSkeleton) return
    if (insuranceLoading) return
    let cancelled = false
    void waitAtLeast(
      tabSkeletonStartedAtRef.current,
      TAB_SKELETON_DURATION_MS,
    ).then(() => {
      if (cancelled) return
      setIsTabSkeleton(false)
    })
    return () => {
      cancelled = true
    }
  }, [isTabSkeleton, tabSkeletonKey, insuranceLoading])

  const onSelectInsurancePlan = (args: RowSelectEventArgs): void => {
    if (!isInsurancePlansProcessing.current) {
      const selectedIndex = args.rowIndex ?? 0
      if (selectedIndex != selectedInsurancePlanIndex.current) {
        uuId.current = uuid()
        selectedInsurancePlanIndex.current = selectedIndex
        forceRerender()
      }
    }
  }

  const onChangeCarrier = (index: number): void => {
    selectedInsurancePlanIndex.current = index
    uuId.current = uuid()
    if (gridRef.current) {
      gridRef.current.selectRows([selectedInsurancePlanIndex.current])
    }
    forceRerender()
  }

  const activateNewlyAddedInsurancePlan = (
    insurancePlansCount: number,
  ): void => {
    if (insurancePlansCount > 0) {
      const newlyAddedIndex = insurancePlansCount - 1
      setTimeout(() => {
        selectedInsurancePlanIndex.current = newlyAddedIndex
        if (currentTabKeyState === 'coverage') {
          onChangeCarrier(selectedInsurancePlanIndex.current)
        } else {
          if (gridRef.current) {
            gridRef.current.selectRows([newlyAddedIndex])
          }
        }
      }, 500)
    }
  }

  const onSaveNewInsurancePlan = (): void => {
    if (!selectedPatient?.patient_id) return
    dispatch(fetchInsurancePlansByPatientId(selectedPatient.patient_id)).then(
      (state) => {
        const familyMemberId = selectedFamilyMemberId
        uuId.current = uuid()
        insuranceData.current = state.payload
        forceRerender()
        if (familyMemberId) {
          const familyMember = state.payload.familyMembers?.find(
            (member: any) => member.member_id === familyMemberId,
          )
          if (familyMember) {
            optionalCompletionAction.current = (): void => {
              showToast(
                createSuccessToast(
                  'Insurance plan added successfully',
                  'Insurance plan has been added successfully.',
                ),
              )
              setShowFamilyMembers(true)
              setSelectedFamilyMemberId(familyMemberId)
              activateNewlyAddedInsurancePlan(
                familyMember.insurancePlans.length,
              )
            }
          }
        } else {
          activateNewlyAddedInsurancePlan(state.payload.insurancePlans.length)
          optionalCompletionAction.current = (): void => {
            showToast(
              createSuccessToast(
                'Insurance plan added successfully',
                'Insurance plan has been added successfully.',
              ),
            )
          }
        }
      },
    )
  }

  const onDeleteInsurancePlan = useCallback(
    async (insurance_plan_id: string): Promise<void> => {
      let sourceInsurancePlans = insuranceData.current.insurancePlans

      if (selectedFamilyMemberId) {
        sourceInsurancePlans =
          insuranceData.current.familyMembers?.find(
            (member: any) => member.member_id === selectedFamilyMemberId,
          )?.insurancePlans ?? []
      }

      const selectedIndex = sourceInsurancePlans.findIndex(
        (plan) => plan.insurance_plan_id === insurance_plan_id,
      )

      let updatedInsuranceState: InsuranceData

      if (selectedFamilyMemberId) {
        updatedInsuranceState = {
          ...insuranceData.current,
          familyMembers:
            insuranceData.current.familyMembers?.map((member) => {
              if (member.member_id === selectedFamilyMemberId) {
                return {
                  ...member,
                  insurancePlans: sourceInsurancePlans
                    .filter(
                      (plan) => plan.insurance_plan_id !== insurance_plan_id,
                    )
                    .map((plan, index) => ({
                      ...plan,
                      number: (index + 1).toString(),
                    })),
                }
              }
              return member
            }) ?? [],
        }
      } else {
        updatedInsuranceState = {
          ...insuranceData.current,
          insurancePlans: sourceInsurancePlans
            .filter((plan) => plan.insurance_plan_id !== insurance_plan_id)
            .map((plan, index) => ({
              ...plan,
              number: (index + 1).toString(),
            })),
        }
      }

      insuranceData.current = updatedInsuranceState

      const updatedPlans = selectedFamilyMemberId
        ? (updatedInsuranceState.familyMembers?.find(
            (member) => member.member_id === selectedFamilyMemberId,
          )?.insurancePlans ?? [])
        : updatedInsuranceState.insurancePlans

      if (selectedIndex >= updatedPlans.length) {
        selectedInsurancePlanIndex.current = updatedPlans.length - 1
      } else {
        selectedInsurancePlanIndex.current = selectedIndex
      }

      uuId.current = uuid()
      forceRerender()

      setTimeout(() => {
        isInsurancePlansProcessing.current = false
      }, 3000)
    },
    [selectedFamilyMemberId],
  )

  const handleAddInsuranceClick = (): void => {
    setIsAddInsuranceVisible(true)
  }

  const handleSubmitClaim = async (): Promise<void> => {
    if (selectedFamilyMemberId) {
      const response = await fetchPatientById(selectedFamilyMemberId)
      const patient = response.data.patient as PatientWithInsurance
      const contact = contacts?.find((c) => c.patient_id === patient.patient_id)
      dispatch(setSelectedPatient({ patient: patient as any, contact }))
      dispatch(toggleSidePanel(true))
    }
    navigate('/claims', {
      state: {
        showAddClaim: true,
      },
    })
  }

  const handleSetPrimaryInsurancePlanClick = (
    insurance_plan_id: string,
  ): void => {
    isInsurancePlansProcessing.current = true
    dispatch(setPrimaryInsurancePlanById(insurance_plan_id))
      .then(() => {
        let sourceInsurancePlans = insuranceData.current.insurancePlans

        if (selectedFamilyMemberId) {
          sourceInsurancePlans =
            insuranceData.current.familyMembers?.find(
              (member: any) => member.member_id === selectedFamilyMemberId,
            )?.insurancePlans ?? []
        }

        const selectedIndex = sourceInsurancePlans.findIndex(
          (plan) => plan.insurance_plan_id === insurance_plan_id,
        )

        let updatedInsuranceState: InsuranceData

        if (selectedFamilyMemberId) {
          updatedInsuranceState = {
            ...insuranceData.current,
            familyMembers:
              insuranceData.current.familyMembers?.map((member) => {
                if (member.member_id === selectedFamilyMemberId) {
                  return {
                    ...member,
                    insurancePlans: sourceInsurancePlans.map((plan) => {
                      return {
                        ...plan,
                        is_primary:
                          plan.insurance_plan_id === insurance_plan_id,
                      }
                    }),
                  }
                }
                return member
              }) ?? [],
          }
        } else {
          updatedInsuranceState = {
            ...insuranceData.current,
            insurancePlans: sourceInsurancePlans.map((plan) => {
              return {
                ...plan,
                is_primary: plan.insurance_plan_id === insurance_plan_id,
              }
            }),
          }
        }

        insuranceData.current = updatedInsuranceState
        selectedInsurancePlanIndex.current = selectedIndex

        optionalCompletionAction.current = (): void => {
          showToast(
            createSuccessToast(
              'Primary insurance plan set successfully',
              'Primary insurance plan has been set successfully.',
            ),
          )
          uuId.current = uuid()
          forceRerender()
        }
        setTimeout(() => {
          isInsurancePlansProcessing.current = false
        }, 2000)
      })
      .catch((error) => {
        logger.error('Error on setting up the primary insurance plan:', error)
        isInsurancePlansProcessing.current = false
      })
  }

  const deleteInsurancePlan = useCallback(async (): Promise<void> => {
    onDeleteInsurancePlan(insurancePlanIdToDelete)
  }, [insurancePlanIdToDelete, onDeleteInsurancePlan])

  const handleDeleteInsurancePlanClick =
    useCallback(async (): Promise<void> => {
      isInsurancePlansProcessing.current = true
      const startedAt = Date.now()
      displayLoading()
      try {
        if (!defaultInsurancePlans.current.includes(insurancePlanIdToDelete)) {
          await dispatch(deleteInsurancePlanById(insurancePlanIdToDelete))
          deleteInsurancePlan()
        } else {
          deleteInsurancePlan()
        }
        optionalCompletionAction.current = (): void => {
          showToast(
            createSuccessToast(
              'Insurance plan deleted successfully',
              'Insurance plan has been deleted successfully.',
            ),
          )
        }
      } catch (error) {
        logger.error('Error deleting insurance plan:', error)
        isInsurancePlansProcessing.current = false
        optionalCompletionAction.current = (): void => {
          showToast(
            createFailedToast(
              'Insurance plan not deleted',
              'Insurance plan not deleted. Please try again.',
            ),
          )
        }
      } finally {
        setShowConfirmDeleteModal(false)
        await waitAtLeast(startedAt, FIRST_LOAD_OVERLAY_MS)
        hideInsuranceLoading()
      }
    }, [
      insurancePlanIdToDelete,
      dispatch,
      deleteInsurancePlan,
      displayLoading,
      hideInsuranceLoading,
      showToast,
    ])

  useLayoutEffect(() => {
    displayPageLoading()
  }, [displayPageLoading])

  useEffect(() => {
    let cancelled = false
    const startedAt = Date.now()
    const resetAndRerender = (): void => {
      if (!isInsurancePlansProcessing.current) {
        selectedInsurancePlanIndex.current = 0
        if (selectedFamilyMemberId) {
          setShowFamilyMembers(false)
          setSelectedFamilyMemberId(undefined)
        } else {
          forceRerender()
        }
      }
    }
    const finish = async (): Promise<void> => {
      await waitAtLeast(startedAt, FIRST_LOAD_OVERLAY_MS)
      if (cancelled) return
      isPageReadyRef.current = true
      setIsPageReady(true)
      hideInsuranceLoading()
    }

    if (!selectedPatient?.patient_id) {
      uuId.current = uuid()
      insuranceData.current = {
        insurancePlans: [],
      }
      resetAndRerender()
      void finish()
      return () => {
        cancelled = true
      }
    }
    isPageReadyRef.current = false
    setIsPageReady(false)
    setIsTabSkeleton(false)
    displayPageLoading()
    dispatch(fetchInsurancePlansByPatientId(selectedPatient.patient_id)).then(
      (state) => {
        if (cancelled) return
        uuId.current = uuid()
        insuranceData.current = state.payload
        resetAndRerender()
        void finish()
      },
    )
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPatient, displayPageLoading, hideInsuranceLoading, dispatch])

  useEffect(() => {
    return () => {
      hideLoading(INSURANCE_LOADING_CONTAINER)
    }
  }, [hideLoading])

  setTimeout(() => {
    try {
      if (gridRef.current && gridRef.current.getSelectedRows().length === 0) {
        gridRef.current.selectRows([selectedInsurancePlanIndex.current])
        const rowCells = document.querySelectorAll('.e-rowcell.e-active')
        rowCells.forEach((cell) => {
          ;(cell as HTMLElement).style.removeProperty('background-color')
        })
      }
    } catch {}
  }, 500)

  const emptyInsuranceElement = (): React.ReactElement => {
    const hasPatient = Boolean(selectedPatient?.patient_id)
    return (
      <div className="insurance-empty-state-wrapper">
        <InsuranceEmptyCard
          iconClass="mgc mgc_shield_fill"
          emptyTitle={
            hasPatient
              ? 'No insurance plans found'
              : 'No insurance data to display'
          }
          description={
            hasPatient
              ? "You don't have any insurance plans on record yet."
              : 'Please select a patient to view their insurance information.'
          }
          actionText={hasPatient ? 'Add plan' : 'Select patient'}
          onAction={
            hasPatient
              ? handleAddInsuranceClick
              : () => dispatch(requestPatientSelection())
          }
        />
      </div>
    )
  }

  useEffect(() => {
    selectedInsurancePlanIndex.current = 0
    uuId.current = uuid()
    setTimeout(() => {
      forceRerender()
    }, 500)
  }, [selectedFamilyMemberId])

  const currentInsurancePlans = (): any[] => {
    if (!selectedFamilyMemberId) {
      return insuranceState.insurancePlans ?? []
    }

    const familyMember = insuranceState.familyMembers?.find(
      (member) => member.member_id === selectedFamilyMemberId,
    )

    return familyMember?.insurancePlans ?? []
  }

  useEffect(() => {
    if (
      showFamilyMembers &&
      insuranceState.familyMembers &&
      insuranceState.familyMembers.length > 0 &&
      !selectedFamilyMemberId
    ) {
      setSelectedFamilyMemberId(insuranceState.familyMembers[0].member_id)
    } else {
      if (!showFamilyMembers) {
        setSelectedFamilyMemberId(undefined)
      }
    }
  }, [insuranceState.familyMembers, selectedFamilyMemberId, showFamilyMembers])

  const showPageEmpty =
    !selectedPatient?.patient_id || currentInsurancePlans().length === 0

  const detailsPlans = currentInsurancePlans()
  const detailsPlan = detailsPlans[selectedInsurancePlanIndex.current]
  const detailsClaims = detailsPlan?.claim_histories ?? []
  const emptyTableCount = [
    detailsPlans.length === 0,
    !detailsPlan,
    detailsClaims.length === 0,
  ].filter(Boolean).length
  const outlineEmptyTables = emptyTableCount >= 2

  return (
    <>
      <Container
        fluid
        className={`insurance-container${isPageReady ? ' is-ready' : ''}`}
        key={currentTabKeyState}
      >
        {pagePreviousState && (
          <GoBackButton previousPageState={pagePreviousState} />
        )}
        <SectionInsuranceAction
          handleAddInsuranceClick={handleAddInsuranceClick}
          onSubmitClaim={handleSubmitClaim}
          hasInsurancePlan={currentInsurancePlans().length > 0}
          {...(Array.isArray(insuranceState.familyMembers) &&
          insuranceState.familyMembers.length > 0
            ? {
                familyMembersInfo: {
                  familyMembers: insuranceState.familyMembers,
                  showFamilyMembers,
                  setShowFamilyMembers,
                  selectedFamilyMemberId,
                  setSelectedFamilyMemberId,
                },
              }
            : {})}
          disabled={!selectedPatient?.patient_id}
        />
        <Tabs
          activeKey={currentTabKeyState}
          onSelect={(key) => {
            if (key === 'coverage') {
              uuId.current = uuid()
            }
            if (location.state) {
              location.state.tabKey = key as string
            }
            setCurrentTabKeyState(key as string)
          }}
        >
          <Tab eventKey="details" title={<>Details</>}>
            {isTabSkeleton ? <InsuranceSkeleton variant="details" /> : null}
            <div hidden={isTabSkeleton} aria-hidden={isTabSkeleton}>
              {showPageEmpty ? (
                emptyInsuranceElement()
              ) : (
                <>
                  <SectionInsurancePlan
                    key={selectedFamilyMemberId}
                    insurancePlans={detailsPlans}
                    onSelectInsurancePlan={onSelectInsurancePlan}
                    gridRef={gridRef}
                    handleSetPrimaryInsurancePlanClick={
                      handleSetPrimaryInsurancePlanClick
                    }
                    handleDeleteInsurancePlanClick={(
                      insurance_plan_id: string,
                    ) => {
                      setInsurancePlanIdToDelete(insurance_plan_id)
                      setShowConfirmDeleteModal(true)
                    }}
                    outlined={outlineEmptyTables}
                  />
                  <SectionInsuranceDetails
                    insurancePlan={detailsPlan}
                    outlined={outlineEmptyTables}
                  />
                  <SectionClaimHistory
                    historyData={detailsClaims}
                    outlined={outlineEmptyTables}
                    onSubmitClaim={handleSubmitClaim}
                  />
                </>
              )}
            </div>
          </Tab>
          <Tab eventKey="coverage" title={<>Coverage</>}>
            {showPageEmpty ? (
              isTabSkeleton ? (
                <InsuranceSkeleton variant="coverage" />
              ) : (
                emptyInsuranceElement()
              )
            ) : (
              <SectionInsuranceCoverage
                insurancePlan={detailsPlan}
                carriers={detailsPlans.map((insurancePlan) => ({
                  value: insurancePlan.insurance_plan_id,
                  label: insurancePlan.carrier_name,
                }))}
                onChangeCarrier={onChangeCarrier}
                uuId={uuId}
                isPending={isTabSkeleton}
              />
            )}
          </Tab>
        </Tabs>
      </Container>
      {isAddInsuranceVisible && (
        <AddInsurance
          isVisible={isAddInsuranceVisible}
          onSaveNewInsurancePlan={onSaveNewInsurancePlan}
          closeModal={() => {
            setIsAddInsuranceVisible(false)
          }}
          currentPayers={currentInsurancePlans().map(
            (insurancePlan) => insurancePlan.payer_id,
          )}
          familyMember={insuranceState.familyMembers?.find(
            (member) => member.member_id === selectedFamilyMemberId,
          )}
        />
      )}
      <DeleteConfirmationModal
        show={showConfirmDeleteModal}
        onHide={() => setShowConfirmDeleteModal(false)}
        onConfirm={handleDeleteInsurancePlanClick}
        title="Delete insurance plan?"
        bodyText={`Are you sure you want to delete the "${
          currentInsurancePlans().find(
            (x) => x.insurance_plan_id === insurancePlanIdToDelete,
          )?.carrier_name || ''
        }" insurance plan? This action can't be undone.`}
      />
    </>
  )
}

export default InsuranceMain
